import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import useAuth from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';
import { type AddSortFields, addSortFields } from '@/hooks/useSortOptions';
import useSystemPlans from '@/hooks/useSystemPlans';
import useUserLocations from '@/hooks/useUserLocations';

import { queryClient } from '@/services';

import { reportsPlansAtom } from '../state';

import {
  type AssignableCourseResult,
  reportPlans,
} from '@/_generated/api-clients/content';
import {
  type ComplianceCourseEnrollmentDto,
  getPlansSummary,
  type Plan,
  type PlanReport,
} from '@/_generated/api-clients/xp';

const isReadonlySubscribed = (planId: string, systemPlans: Plan[]) =>
  systemPlans.some(
    systemPlan => systemPlan.readonly && systemPlan.id === planId,
  );

// @TODO Remove when in-house compliance epic is completed
const toReportPlan = (course: AssignableCourseResult): PlanReport => ({
  activeCertificates: 0,
  category: 'compliance',
  id: course.id ?? '',
  legacyId: course.courseID,
  ownerId: '',
  ownerType: 'SELF',
  readonly: false,
  created: course.createdDate ?? '',
  name: course.courseName ?? {},
  type: 'COMPLIANCE',
});

export type ReportsPlanRow = AddSortFields<PlanReport> & {
  isReadonlySubscribed: boolean;
  legacyEnrollments?: Record<string, ComplianceCourseEnrollmentDto>;
  isEnabled?: boolean;
};

export const useGetPlansSummary = () => {
  const { xpCompliancePlans: inHouseCompliancePlansFF } = useFlags();
  const { licenseeModeToggle: isLicenseeMode } = useAtomValue(reportsPlansAtom);
  const {
    data: auth,
    isFetching: authFetching,
    isPending: authPending,
  } = useAuth();
  const isLicenseeUser = auth?.isLicensee;
  const userLocations = useUserLocations({ isLicenseeMode });
  const { systemPlans, isFetchingSystemPlans } = useSystemPlans();

  const useOld =
    !inHouseCompliancePlansFF && !(isLicenseeMode || isLicenseeUser);

  const enabled =
    userLocations.length > 0 &&
    !authFetching &&
    !authPending &&
    !isFetchingSystemPlans;

  return useQuery({
    queryKey: ['reportsPlans', userLocations, useOld],
    queryFn: () =>
      Promise.allSettled([
        // @TODO Legacy content API for Compliance courses - remove when in-house compliance epic is completed
        useOld
          ? reportPlans({ query: { locationIds: userLocations } }).then(
              response => response.data?.courses ?? [],
            )
          : ([] as AssignableCourseResult[]),
        getPlansSummary({ query: { locationNumbers: userLocations } }).then(
          response => response.data ?? [],
        ),
      ]),
    enabled,
    select: ([plansOld, plansXp]): ReportsPlanRow[] => {
      const storePlans = (plansXp.status === 'fulfilled' ? plansXp.value : [])
        .filter(
          plan =>
            (!inHouseCompliancePlansFF ? plan.type !== 'COMPLIANCE' : true) &&
            (plan.type === 'STORE' ||
              isReadonlySubscribed(plan.id, systemPlans)),
        )
        .map(report => ({
          ...addSortFields(report),
          isReadonlySubscribed: isReadonlySubscribed(report.id, systemPlans),
        }));

      // @TODO Remove when in-house compliance epic is completed
      const compliancePlans = (
        plansOld.status === 'fulfilled' ? plansOld.value : []
      ).map(course => ({
        ...addSortFields(toReportPlan(course)),
        isReadonlySubscribed: false,
        isEnabled: course.enabled !== false,
        legacyEnrollments: course.enrollments,
      }));

      return [...storePlans, ...compliancePlans];
    },
  });
};

export const invalidateReportsPlans = () =>
  queryClient.invalidateQueries({
    queryKey: ['reportsPlans'],
    refetchType: 'all',
  });
